import { useEffect, useRef, useState } from 'react';
import { useQuery } from 'react-query';
import { FilterMatchMode } from 'primereact/api';
import styled from 'styled-components';
import { Messages } from 'primereact/messages';
import { Button } from 'primereact/button';
import { Tag as DefaultTag } from 'primereact/tag';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Link } from 'react-router-dom';
import { getAllUsers } from '../../../services/user';
import { PageTitle } from '../../Headings';
import TrueFalseIcon from '../../TrueFalseIcon';
import AddUserModal from '../AddUserModal';
import SearchField from '../../DataTable/searchField';
import { TitleWrapper } from '../../Headings/common';
import { Role, User } from '../../../services/model';

const Tag = styled(DefaultTag)`
  margin-right: 6px;
  margin-bottom: 2px;
`;

const HeaderWrapper = styled.div`
  display: flex;
  justify-content: space-between;
`;

function UserList() {
  const [filters, setFilters] = useState({
    global: { value: '', matchMode: FilterMatchMode.CONTAINS },
  });
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
  const { isFetching, error: loadingError, data } = useQuery('userList', getAllUsers);
  const messages = useRef<Messages>(null);

  const handleGlobalSearch = (value: string) => {
    setFilters({ ...filters, global: { ...filters.global, value } });
  };

  const customHeader = () => (
    <HeaderWrapper>
      <div />
      <SearchField onChange={handleGlobalSearch} />
    </HeaderWrapper>
  );

  const renderRoleList = (roles: Role[]) => {
    if (roles?.length > 0) {
      return roles.map((item: Role) => <Tag key={item.id} value={item.name} />);
    }

    return <span>-</span>;
  };

  useEffect(() => {
    if (loadingError) {
      messages.current?.show({ severity: 'error', summary: 'An error has occurred while loading data!', sticky: true });
    }
  }, [loadingError]);

  return (
    <>
      <TitleWrapper>
        <PageTitle>User List</PageTitle>
        <div>
          <Button label="User" icon="pi pi-plus" size="small" onClick={() => setIsAddUserModalOpen(true)} />
        </div>
      </TitleWrapper>
      <Messages ref={messages} />
      <AddUserModal open={isAddUserModalOpen} onClose={() => setIsAddUserModalOpen(false)} />
      <DataTable
        value={data}
        header={customHeader}
        filters={filters}
        loading={isFetching}
        rows={25}
        size="small"
        globalFilterFields={['email', 'name', 'familyName']}
        rowHover
        paginator
        sortField='lastLogin'
        sortOrder={-1}
      >
        <Column
          header="E-mail"
          field="email"
          /* eslint-disable  react/no-unstable-nested-components */
          body={(rowData: User) => <Link to={`/admin/user/${rowData.id}`}>{rowData.email}</Link>}
          sortable
        />
         <Column 
          header="Last Login" 
          field="lastLogin" 
          sortable 
          body={(rowData: User) => {
            const formattedDate = rowData.lastLogin 
                ? new Date(rowData.lastLogin).toLocaleDateString() 
                : ''; 
            return <span>{formattedDate}</span>;
          }}
        />
        <Column header="Name" field="name" sortable />
        <Column header="Surname" field="familyName" sortable />
        <Column
          header="Active"
          field="isActive"
          align="center"
          sortable
          /* eslint-disable  react/no-unstable-nested-components */
          body={({ isActive }: User) => <TrueFalseIcon isValid={isActive} />}
        />
        <Column
          header="Admin"
          field="isAdmin"
          align="center"
          sortable
          /* eslint-disable  react/no-unstable-nested-components */
          body={({ isAdmin }: User) => <TrueFalseIcon isValid={isAdmin} />}
        />
        <Column header="Roles" field="roles" body={({ roles }: User) => renderRoleList(roles)} />
      </DataTable>
    </>
  );
}

export default UserList;
